import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, X } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import NotificationsBell from "./NotificationsBell";
import SettingsIconLink from "./SettingsIconLink";

const MedimagicIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <path d="M40 6 L74 40 L40 74 L6 40 Z" stroke="#3072FF" strokeWidth="5.5" fill="none" strokeLinejoin="round" />
    <polyline points="12,40 25,40 31,20 40,60 49,40 62,40 68,40" stroke="#43E0FF" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NAV_ITEMS = [
  { label: "Dashboard",  path: "/dashboard"  },
  {
    label: "Master Data",
    children: [
      { label: "Country",            path: "/master-data/countries"          },
      { label: "State",              path: "/master-data/states"             },
      { label: "City",               path: "/master-data/cities"             },
      { label: "University",         path: "/master-data/universities"       },
      { label: "Subjects",           path: "/master-data/subjects"           },
      { label: "Medimagic Lessons",  path: "/master-data/medimagic-lessons"  },
      { label: "NMC Lessons",        path: "/master-data/nmc-lessons"        },
      { label: "Colleges",           path: "/master-data/colleges"           },
    ],
  },
  { label: "Users",     path: "/users"     },
  { label: "Timetable", path: "/timetable" },
  { label: "Tickets",   path: "/tickets"   },
  { label: "Contact", path: "/contact" },
];

export default function TopNav() {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [masterOpen, setMasterOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMasterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMasterOpen(false);
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isMasterActive = () => location.pathname.startsWith("/master-data");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <>
    <header className="topnav">
      <div className="topnav-inner">
        {/* Logo */}
        <Link to="/dashboard" className="topnav-logo">
          <MedimagicIcon size={38} />
          <div className="topnav-logo-text">
            <span className="topnav-logo-name">medimagic</span>
            <span className="topnav-logo-sub">dashboard</span>
          </div>
        </Link>

        {/* Nav links — desktop only */}
        <nav className="topnav-links">
          {NAV_ITEMS.map((item) => {
            if (item.children) {
              return (
                <div
                  key={item.label}
                  className="topnav-dropdown-wrap"
                  ref={dropdownRef}
                >
                  <button
                    className={`topnav-link topnav-link-btn${isMasterActive() ? " active" : ""}`}
                    onClick={() => setMasterOpen((v) => !v)}
                    data-testid="master-data-menu"
                  >
                    {item.label}
                    <ChevronDown
                      size={14}
                      className={`topnav-chevron${masterOpen ? " open" : ""}`}
                    />
                  </button>

                  {masterOpen && (
                    <div className="topnav-dropdown">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`topnav-dropdown-item${isActive(child.path) ? " active" : ""}`}
                          data-testid={`nav-${child.label.toLowerCase()}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`topnav-link${isActive(item.path) ? " active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="topnav-user">
          <NotificationsBell to="/notifications" />
          <SettingsIconLink to="/settings" />
          <div className="topnav-avatar">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </div>
          <span className="topnav-username">
            {user?.name || user?.email}
          </span>
          <button
            className="topnav-logout"
            onClick={handleLogout}
            data-testid="logout-btn"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>

        {/* Hamburger — mobile only */}
        <button
          className="topnav-hamburger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="topnav-hamburger"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </header>

      {/* Mobile slide-down menu — rendered as sibling outside <header> */}
      {mobileOpen && (
        <nav className="topnav-mobile-menu" data-testid="topnav-mobile-menu">
          {NAV_ITEMS.map((item) => {
            if (item.children) {
              return (
                <div key={item.label}>
                  <button
                    className={`topnav-mobile-link topnav-mobile-parent${isMasterActive() ? " active" : ""}`}
                    onClick={() => setMasterOpen((v) => !v)}
                  >
                    {item.label}
                    <ChevronDown size={14} className={`topnav-chevron${masterOpen ? " open" : ""}`} />
                  </button>
                  {masterOpen && (
                    <div className="topnav-mobile-sub">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`topnav-mobile-sublink${isActive(child.path) ? " active" : ""}`}
                          onClick={() => setMobileOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`topnav-mobile-link${isActive(item.path) ? " active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      )}
    </>
  );
}
